// NEW_FILE_JUSTIFIED: New messages feature component

import { memo, useState, useCallback, type MouseEvent, type ReactNode } from 'react';
import { Download, FileText, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { MediaLightbox } from '@/components/messages/MediaLightbox';
import { QuotedMessage } from '@/components/messages/QuotedMessage';
import { DeletedMessagePlaceholder } from '@/components/messages/DeletedMessagePlaceholder';
import type { IMessageBubbleProps, IMediaContentProps } from '@/@types/messages';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function downloadFile(url: string, filename: string) {
  fetch(url)
    .then(r => r.blob())
    .then(blob => {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    })
    .catch(() => {
      // Last resort: use anchor with download attribute (still same tab)
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
}

function isPdf(mimeType?: string, filename?: string): boolean {
  if (mimeType === 'application/pdf') return true;
  if (filename?.toLowerCase().endsWith('.pdf')) return true;
  return false;
}

const URL_REGEX = /https?:\/\/[^\s<>)"']+/g;

function linkifyText(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    parts.push(
      <a
        key={match.index}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 underline underline-offset-2 break-all hover:opacity-80"
      >
        {url}
      </a>
    );
    lastIndex = match.index + url.length;
  }

  if (lastIndex === 0) return text;
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return <>{parts}</>;
}

function isImageMime(mimeType?: string, filename?: string): boolean {
  if (mimeType?.startsWith('image/')) return true;
  const ext = filename?.toLowerCase().split('.').pop();
  return ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'gif' || ext === 'webp';
}

const MediaContent = memo(function MediaContent({ message, onOpenLightbox }: IMediaContentProps) {
  const {
    mediaType,
    mediaUrl,
    mediaFilename,
    mediaMimeType,
    mediaSize,
    imageThumbnail,
    text,
    isOutgoing,
  } = message;

  return (
    <>
      {message.groupSenderName && !isOutgoing && (
        <p className="text-xs font-semibold text-accent-orange mb-1">{message.groupSenderName}</p>
      )}

      {/* Quoted reply block */}
      {message.replyToMessageId !== undefined && (
        <QuotedMessage
          senderName={message.replyToSenderName}
          text={message.replyToText}
          isOutgoing={isOutgoing}
        />
      )}

      {/* Image: clickable thumbnail → lightbox + download */}
      {mediaType === 'image' && (imageThumbnail || mediaUrl) && (
        <div className="relative group mb-2">
          <button
            type="button"
            className="block rounded-lg max-w-full overflow-hidden cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            onClick={() => mediaUrl && onOpenLightbox()}
            aria-label="View full image"
          >
            <img
              src={mediaUrl ?? `data:image/jpeg;base64,${imageThumbnail}`}
              alt="Image attachment"
              className="rounded-lg max-w-full"
            />
          </button>
          {mediaUrl && (
            <Button
              variant="secondary"
              size="icon"
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity size-8 bg-black/60 hover:bg-black/80 text-white"
              aria-label="Download image"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                downloadFile(mediaUrl, mediaFilename ?? 'image.jpg');
              }}
            >
              <Download className="size-4" />
            </Button>
          )}
        </div>
      )}

      {/* Video: thumbnail with play overlay → lightbox + download */}
      {mediaType === 'video' && (
        <div className="relative group mb-2">
          <button
            type="button"
            className="relative block rounded-lg max-w-full overflow-hidden cursor-pointer bg-black/10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            onClick={() => mediaUrl && onOpenLightbox()}
            aria-label="Play video"
          >
            {imageThumbnail ? (
              <img
                src={`data:image/jpeg;base64,${imageThumbnail}`}
                alt="Video thumbnail"
                className="rounded-lg max-w-full opacity-80"
              />
            ) : (
              <div className="w-48 h-32 flex items-center justify-center rounded-lg bg-muted" />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-full bg-black/60 p-3">
                <Play className="size-6 text-white fill-white" aria-hidden="true" />
              </div>
            </div>
          </button>
          {mediaUrl && (
            <Button
              variant="secondary"
              size="icon"
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity size-8 bg-black/60 hover:bg-black/80 text-white"
              aria-label="Download video"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                downloadFile(mediaUrl, mediaFilename ?? 'video.mp4');
              }}
            >
              <Download className="size-4" />
            </Button>
          )}
        </div>
      )}

      {/* Audio: inline player */}
      {mediaType === 'audio' && mediaUrl && (
        <div className="mb-2">
          <audio
            controls
            className="max-w-full"
            preload="metadata"
            aria-label={mediaFilename ?? 'Audio message'}
          >
            <source src={mediaUrl} type={mediaMimeType ?? 'audio/ogg'} />
          </audio>
        </div>
      )}

      {/* Document: image preview, PDF preview, or file card with download */}
      {mediaType === 'document' && (
        <div className="mb-2">
          {/* Image files sent as documents (PNG, JPG, etc.) */}
          {mediaUrl && isImageMime(mediaMimeType, mediaFilename) && (
            <div className="relative group mb-2">
              <button
                type="button"
                className="block rounded-lg max-w-full overflow-hidden cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                onClick={() => onOpenLightbox()}
                aria-label="View full image"
              >
                <img
                  src={mediaUrl}
                  alt={mediaFilename ?? 'Image attachment'}
                  className="rounded-lg max-w-full"
                />
              </button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity size-8 bg-black/60 hover:bg-black/80 text-white"
                aria-label="Download image"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  downloadFile(mediaUrl, mediaFilename ?? 'image');
                }}
              >
                <Download className="size-4" />
              </Button>
            </div>
          )}
          {/* PDF inline preview */}
          {mediaUrl && isPdf(mediaMimeType, mediaFilename) && (
            <div className="rounded-lg overflow-hidden mb-2 border border-border">
              <iframe
                src={`${mediaUrl}#toolbar=0&navpanes=0`}
                title={mediaFilename ?? 'PDF preview'}
                className="w-full h-[300px] bg-white"
              />
            </div>
          )}
          {/* File card (always shown for documents) */}
          <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3 min-w-[200px]">
            <div className="rounded-lg bg-primary/10 p-2.5 shrink-0">
              <FileText className="size-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{mediaFilename ?? 'Document'}</p>
              {mediaSize !== undefined && (
                <p className="text-xs text-muted-foreground">{formatFileSize(mediaSize)}</p>
              )}
            </div>
            {mediaUrl && (
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label={`Download ${mediaFilename ?? 'document'}`}
                onClick={(e: MouseEvent) => {
                  e.preventDefault();
                  downloadFile(mediaUrl, mediaFilename ?? 'document');
                }}
              >
                <Download className="size-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Sticker: inline image (no lightbox) */}
      {mediaType === 'sticker' && mediaUrl && (
        <img src={mediaUrl} alt="Sticker" className="max-w-[160px] max-h-[160px] mb-2" />
      )}

      {/* Legacy: image thumbnail without mediaType (backward compat for old messages) */}
      {!mediaType && imageThumbnail !== undefined && (
        <img
          src={`data:image/jpeg;base64,${imageThumbnail}`}
          alt="Attachment preview"
          className={cn('rounded-lg max-w-full', text ? 'mb-2' : 'mb-0')}
        />
      )}

      {/* Text content */}
      {text && (
        <p
          className={cn(
            'text-foreground text-sm leading-snug whitespace-pre-wrap',
            isOutgoing ? 'font-medium' : 'font-normal'
          )}
        >
          {linkifyText(text)}
        </p>
      )}

      {/* Link preview card */}
      {message.linkPreviewUrl && (message.linkPreviewTitle || message.linkPreviewDescription) && (
        <a
          href={message.linkPreviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-2 rounded-lg overflow-hidden border-l-[3px] border-l-primary bg-muted/50 hover:bg-muted/80 transition-colors no-underline"
        >
          <div className="px-3 py-2">
            {message.linkPreviewSiteName && (
              <p className="text-xs font-medium text-primary mb-0.5">
                {message.linkPreviewSiteName}
              </p>
            )}
            {message.linkPreviewTitle && (
              <p className="text-sm font-semibold text-foreground leading-snug line-clamp-2">
                {message.linkPreviewTitle}
              </p>
            )}
            {message.linkPreviewDescription && (
              <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-3">
                {message.linkPreviewDescription}
              </p>
            )}
          </div>
          {message.linkPreviewImageThumbnail && (
            <img
              src={`data:image/jpeg;base64,${message.linkPreviewImageThumbnail}`}
              alt={message.linkPreviewTitle ?? 'Link preview'}
              className="w-full max-h-[200px] object-cover"
            />
          )}
        </a>
      )}
    </>
  );
});

export const MessageBubble = memo(function MessageBubble({ message }: IMessageBubbleProps) {
  const { isOutgoing, timestamp, senderName, imageThumbnail, mediaType, mediaUrl, mediaFilename } =
    message;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const handleOpenLightbox = useCallback(() => setLightboxOpen(true), []);

  // Show placeholder for deleted-for-everyone messages
  if (message.deletedForEveryone) {
    return (
      <div
        role="article"
        aria-label={`Deleted message at ${timestamp}`}
        className={cn(
          'flex max-w-[75%] w-fit',
          isOutgoing ? 'justify-end ml-auto' : 'items-end gap-2'
        )}
      >
        <div>
          <div
            className={cn(
              'bg-muted/50 border border-border px-4 py-3',
              isOutgoing
                ? 'rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-sm'
                : 'rounded-tl-2xl rounded-tr-2xl rounded-br-2xl rounded-bl-sm'
            )}
          >
            <DeletedMessagePlaceholder />
          </div>
          <p
            className={cn(
              'mt-1',
              isOutgoing ? 'text-foreground/70 text-right' : 'text-muted-foreground'
            )}
            style={{ fontSize: '11px' }}
          >
            {timestamp}
          </p>
        </div>
      </div>
    );
  }

  const hasReactions = message.reactions !== undefined && message.reactions.length > 0;
  const hasMedia =
    mediaType !== undefined || imageThumbnail !== undefined || message.linkPreviewUrl !== undefined;

  const reactionBadge = hasReactions ? (
    <div
      role="img"
      aria-label={`Reactions: ${(message.reactions ?? []).map(r => r.emoji).join(' ')}`}
      className={cn(
        'flex w-fit items-center gap-0.5 rounded-full bg-muted border border-border px-1.5 py-0.5',
        'text-sm leading-none -mt-2 relative z-10',
        isOutgoing ? 'ml-auto mr-2' : 'ml-2'
      )}
    >
      {(message.reactions ?? []).map((r, i) => (
        <span key={i} aria-hidden="true">
          {r.emoji}
        </span>
      ))}
    </div>
  ) : null;

  const isDocImage = mediaType === 'document' && isImageMime(message.mediaMimeType, mediaFilename);
  const lightboxMediaType: 'image' | 'video' | undefined =
    mediaType === 'image' || isDocImage ? 'image' : mediaType === 'video' ? 'video' : undefined;

  const lightbox =
    mediaUrl && lightboxMediaType !== undefined ? (
      <MediaLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        mediaUrl={mediaUrl}
        mediaType={lightboxMediaType}
        filename={mediaFilename}
      />
    ) : null;

  if (isOutgoing) {
    return (
      <>
        <div
          role="article"
          aria-label={`Your message at ${timestamp}`}
          className="flex justify-end max-w-[75%] w-fit ml-auto"
        >
          <div>
            <div
              className={cn(
                'bg-msg-bubble-outgoing border border-msg-bubble-outgoing-border',
                'rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-sm',
                hasMedia ? 'p-1.5 pb-3 [&>p]:px-2.5' : 'px-4 py-3'
              )}
            >
              <MediaContent message={message} onOpenLightbox={handleOpenLightbox} />
            </div>
            {reactionBadge}
            <p
              className={cn('text-foreground/70 text-right', hasReactions ? 'mt-0.5' : 'mt-1')}
              style={{ fontSize: '11px' }}
            >
              {timestamp}
            </p>
          </div>
        </div>
        {lightbox}
      </>
    );
  }

  return (
    <>
      <div
        role="article"
        aria-label={`Message from ${senderName} at ${timestamp}`}
        className="flex items-end gap-2 max-w-[75%] w-fit"
      >
        <div>
          <div
            className={cn(
              'bg-msg-bubble-incoming border border-msg-bubble-incoming-border',
              'rounded-tl-2xl rounded-tr-2xl rounded-br-2xl rounded-bl-sm',
              hasMedia ? 'p-1.5 pb-3 [&>p]:px-2.5' : 'px-4 py-3'
            )}
          >
            <MediaContent message={message} onOpenLightbox={handleOpenLightbox} />
          </div>
          {reactionBadge}
          <p
            className={cn('text-muted-foreground', hasReactions ? 'mt-0.5' : 'mt-1')}
            style={{ fontSize: '11px' }}
          >
            {timestamp}
          </p>
        </div>
      </div>
      {lightbox}
    </>
  );
});
